import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Heart, Eye, Ban, Menu, X } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import UserMenu from './UserMenu';

type TabType = 'search' | 'saved' | 'viewed' | 'discarded';

interface AppNavbarProps {
  activeTab?: TabType;
  onTabChange?: (tab: TabType) => void;
  savedCount?: number;
  viewedCount?: number;
  discardedCount?: number;
  onLogoClick?: () => void;
  showTabs?: boolean;
  leftContent?: React.ReactNode;
  rightContent?: React.ReactNode;
  isVisible?: boolean;
}

const tabs = [
  { id: 'search' as TabType, label: 'Search', icon: Search },
  { id: 'saved' as TabType, label: 'Saved', icon: Heart },
  { id: 'viewed' as TabType, label: 'Viewed', icon: Eye },
  { id: 'discarded' as TabType, label: 'Discarded', icon: Ban },
];

const AppNavbar: React.FC<AppNavbarProps> = ({
  activeTab,
  onTabChange,
  savedCount = 0,
  viewedCount = 0,
  discardedCount = 0,
  onLogoClick,
  showTabs = true,
  leftContent,
  rightContent,
  isVisible = true,
}) => {
  const { isDark } = useTheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const getCount = (tabId: TabType): number | null => {
    switch (tabId) {
      case 'saved': return savedCount > 0 ? savedCount : null;
      case 'viewed': return viewedCount > 0 ? viewedCount : null;
      case 'discarded': return discardedCount > 0 ? discardedCount : null;
      default: return null;
    }
  };

  const handleTabClick = (tab: TabType) => {
    onTabChange?.(tab);
    setMobileMenuOpen(false);
  };

  return (
    <>
      <div
        className={`fixed top-0 left-0 right-0 z-50 bg-white dark:bg-slate-900 border-b border-gray-200 dark:border-gray-800 transition-transform duration-300 ease-out ${
          isVisible ? 'translate-y-0' : '-translate-y-full'
        }`}
      >
        <div className="max-w-[1600px] mx-auto w-full">
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center justify-between h-14 px-6">
            <div className="flex items-center gap-4 flex-shrink-0">
              <Link
                to="/"
                className="inline-flex flex-shrink-0"
                aria-label="Turing home"
                onClick={onLogoClick}
              >
                <img
                  src={isDark ? '/turing-logo-white.svg' : '/turing-logo.svg'}
                  alt="Turing"
                  className="h-5 w-auto"
                />
              </Link>
              {leftContent}
            </div>

            {/* Navigation Tabs - Center */}
            {showTabs && (
              <nav className="flex items-center gap-1">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  const count = getCount(tab.id);
                  return (
                    <button
                      key={tab.id}
                      type="button"
                      onClick={() => handleTabClick(tab.id)}
                      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-gray-100'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-800/50 hover:text-gray-900 dark:hover:text-gray-100'
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                      <span>{tab.label}</span>
                      {count !== null && (
                        <span
                          className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-medium rounded-full ${
                            isActive
                              ? 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                          }`}
                        >
                          {count > 99 ? '99+' : count}
                        </span>
                      )}
                    </button>
                  );
                })}
              </nav>
            )}

            {/* Right section */}
            <div className="flex items-center gap-3">
              {rightContent}
              <UserMenu variant="compact" />
            </div>
          </div>

          {/* Mobile Navigation */}
          <div className="lg:hidden">
            <div className="flex items-center justify-between h-14 px-4">
              <div className="flex items-center gap-2">
                {showTabs ? (
                  <button
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    className="p-2 -ml-2 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-800"
                    aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                  >
                    {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                  </button>
                ) : (
                  <div className="w-9" />
                )}

                <Link
                  to="/"
                  className="inline-flex flex-shrink-0"
                  aria-label="Turing home"
                  onClick={onLogoClick}
                >
                  <img
                    src={isDark ? '/turing-logo-white.svg' : '/turing-logo.svg'}
                    alt="Turing"
                    className="h-5 w-auto"
                  />
                </Link>
              </div>

              <UserMenu variant="compact" />
            </div>

            {/* Mobile menu dropdown */}
            {showTabs && mobileMenuOpen && (
              <div className="border-t border-gray-200 dark:border-gray-800 py-2 px-4 bg-white dark:bg-slate-900">
                <nav className="flex flex-col gap-1">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    const isActive = activeTab === tab.id;
                    const count = getCount(tab.id);
                    return (
                      <button
                        key={tab.id}
                        type="button"
                        onClick={() => handleTabClick(tab.id)}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                          isActive
                            ? 'bg-gray-100 dark:bg-slate-800 text-gray-900 dark:text-gray-100'
                            : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-slate-800/50'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="flex-1 text-left">{tab.label}</span>
                        {count !== null && (
                          <span
                            className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-medium rounded-full ${
                              isActive
                                ? 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                            }`}
                          >
                            {count > 99 ? '99+' : count}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </nav>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Spacer to prevent content from going under fixed navbar */}
      <div className={`${showTabs && mobileMenuOpen ? 'h-[200px]' : 'h-14'}`} aria-hidden />
    </>
  );
};

export default AppNavbar;
